import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Sober DS (v2) — Button. CANONICAL primitive pattern for v2:
 * CVA variants on sober-* tokens, native <button> (keyboard a11y), 1px primary
 * focus ring, no colored borders beyond the outline/primary-outline variant.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-so-md font-bold transition-[background-color,opacity,border-color] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sober-primary-fg focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary:
          'bg-sober-primary-fg text-white hover:bg-sober-primary-hover active:bg-sober-primary-hover',
        secondary:
          'bg-sober-surface border border-sober-border text-sober-text hover:bg-sober-app',
        danger: 'bg-sober-red-fg text-white hover:bg-sober-red-hover active:bg-sober-red-hover',
        outline:
          'border border-sober-primary-fg text-sober-primary bg-transparent hover:bg-sober-primary-bg',
        ghost: 'text-sober-secondary hover:bg-sober-muted hover:text-sober-text',
        link: 'text-sober-primary underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-8 px-3 text-so-12 [&_svg]:size-4',
        md: 'h-11 px-5 text-so-14 [&_svg]:size-4',
        lg: 'h-11 px-8 text-so-14 [&_svg]:size-5',
        icon: 'h-10 w-10 [&_svg]:size-5',
        'icon-sm': 'h-8 w-8 rounded-so-sm [&_svg]:size-4',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />
    );
  }
);
Button.displayName = 'Button';

export { buttonVariants };
